import { clipTimeSec, notify, state } from './state.ts';
import { syncAllToFrame } from './clips.ts';

/**
 * Playback controller. Frames are the unit; a wall-clock rAF loop advances the
 * playhead at timeline fps and loops inside the marked range. While playing,
 * each video runs free and only gets corrected when it drifts noticeably
 * (avoids stutter); while paused, scrubs drive coalesced seeks so the display
 * converges on the exact frame.
 */

let rafId = 0;
let clockStart = 0;
let frameStart = 0;

const DRIFT_TOLERANCE_SEC = 0.15;

export function setFrame(f: number): void {
  const last = Math.max(0, state.totalFrames - 1);
  state.frame = Math.min(Math.max(Math.round(f), 0), last);
  if (!state.playing && !state.exporting) syncAllToFrame(state.frame);
  notify();
}

export function stepFrame(delta: number): void {
  pause();
  setFrame(state.frame + delta);
}

export function play(): void {
  if (state.playing || state.totalFrames === 0 || state.exporting) return;
  state.playing = true;
  const out = Math.max(state.markOut, state.markIn);
  if (state.frame < state.markIn || state.frame >= out) state.frame = state.markIn;
  clockStart = performance.now();
  frameStart = state.frame;
  for (const clip of state.clips) {
    if (!clip.video || clip.error) continue;
    const t = (state.frame - clip.offsetFrames) / state.fps;
    if (t >= 0 && t <= clip.durationSec) {
      clip.video.currentTime = t;
      void clip.video.play().catch(() => {});
    }
  }
  rafId = requestAnimationFrame(tick);
  notify();
}

export function pause(): void {
  if (!state.playing) return;
  state.playing = false;
  cancelAnimationFrame(rafId);
  for (const clip of state.clips) clip.video?.pause();
  syncAllToFrame(state.frame);
  notify();
}

export function toggle(): void {
  if (state.playing) pause();
  else play();
}

function tick(now: number): void {
  if (!state.playing) return;
  const markIn = state.markIn;
  const markOut = Math.max(state.markOut, markIn);
  let f = frameStart + ((now - clockStart) / 1000) * state.fps;

  if (f > markOut) {
    // Loop the marked range; restart every clip's clock at the loop point.
    f = markIn;
    frameStart = markIn;
    clockStart = now;
    for (const clip of state.clips) {
      if (!clip.video || clip.error) continue;
      clip.video.currentTime = clipTimeSec(clip, f);
    }
  }

  state.frame = Math.floor(f);

  for (const clip of state.clips) {
    if (!clip.video || clip.error) continue;
    const t = (f - clip.offsetFrames) / state.fps;
    if (t < 0 || t > clip.durationSec) {
      // Out of this clip's span — hold its edge frame (already decoded).
      if (!clip.video.paused) clip.video.pause();
    } else if (clip.video.paused) {
      clip.video.currentTime = t;
      void clip.video.play().catch(() => {});
    } else if (Math.abs(clip.video.currentTime - t) > DRIFT_TOLERANCE_SEC) {
      clip.video.currentTime = t;
    }
  }

  notify();
  rafId = requestAnimationFrame(tick);
}

// ---------- marks ----------

export function markInHere(): void {
  state.markIn = Math.min(state.frame, state.markOut);
  notify();
}

export function markOutHere(): void {
  state.markOut = Math.max(state.frame, state.markIn);
  notify();
}

export function setMarkIn(f: number): void {
  const last = Math.max(0, state.totalFrames - 1);
  state.markIn = Math.min(Math.max(Math.round(f), 0), Math.min(state.markOut, last));
  notify();
}

export function setMarkOut(f: number): void {
  const last = Math.max(0, state.totalFrames - 1);
  state.markOut = Math.max(Math.min(Math.round(f), last), state.markIn);
  notify();
}

export function resetMarks(): void {
  state.markIn = 0;
  state.markOut = Math.max(0, state.totalFrames - 1);
  notify();
}
